import React from 'react'
import AppRoutes from '../../../../../routes/appRoutes'
import { removeStepsAndRequests } from '../../../../../utils/handleOnDone'

export const updateStepState = ({
    steps,
    setSteps,
    currentStep,
    newStep,
    newDescription,
}) => {
    let hasOneFinish = false
    const updatedSteps = steps?.map(step => {
        const stepDescription =
            step?.name === currentStep ? newDescription : step?.description
        if (step?.name === currentStep) {
            return {
                ...step,
                active: false,
                finish: true,
                description: stepDescription,
            }
        }
        if (step?.name === newStep) {
            hasOneFinish = true
            return {
                ...step,
                active: true,
                finish: false,
                description: stepDescription,
            }
        }
        if (hasOneFinish) {
            return {
                ...step,
                active: false,
                finish: false,
                description: stepDescription,
            }
        }
        return {
            ...step,
            active: false,
            finish: true,
            description: stepDescription,
        }
    })

    setSteps(updatedSteps)
}

export const handleNextStep = ({
    steps,
    setSteps,
    setError,
    setSkipDuplicate,
    selectedAsset,
    handleGetAssetInfo,
    handleChangeAlert,
    formRef,
    history,
    handleChangeOpen,
    currentStep,
    newStep,
}) => {
    const activeStep = steps?.find(f => f.active === true)

    const selectedAssetInfo =
        Array.isArray(selectedAsset) && selectedAsset.length > 0
            ? selectedAsset[0]
            : null

    if (!selectedAssetInfo) {
        setError('No asset selected. Please select an asset before proceeding.')
        return
    }

    let selectedAssetName = selectedAssetInfo?.name || 'Unknown Asset'
    if (selectedAssetInfo?.customer_alias || selectedAssetInfo?.customerAlias) {
        selectedAssetName = selectedAssetInfo?.customer_alias
            ? selectedAssetInfo?.customer_alias
            : selectedAssetInfo?.customerAlias
    }

    // If currentStep and newStep are provided, handle direct transition
    if (currentStep && newStep) {
        const description =
            currentStep === 'Check Duplicates'
                ? 'There are no duplicates'
                : selectedAssetName
        updateStepState({
            steps,
            setSteps,
            currentStep,
            newStep,
            newDescription: description,
        })
        return
    }

    if (activeStep) {
        switch (activeStep?.name) {
            case 'Select Asset':
                if (!selectedAssetInfo) {
                    setError('Please select an asset before you continue')
                    return
                }
                updateStepState({
                    steps,
                    setSteps,
                    currentStep: activeStep?.name,
                    newStep: 'Check Duplicates',
                    newDescription: selectedAssetName,
                })
                handleGetAssetInfo()
                break

            case 'Check Duplicates':
                if (setSkipDuplicate) {
                    handleChangeAlert({
                        message: '',
                        title: (
                            <p className="text-textDarkerGray font-semibold text-lg">
                                Is the request you&apos;re creating <br /> a
                                duplicate?
                            </p>
                        ),
                        textOk: "No, it's not a duplicate",
                        textCancel: "Yes, it's a duplicate",
                        onCancel: () => {
                            handleCancel(history)
                        },
                        onDone: () => {
                            updateStepState({
                                steps,
                                setSteps,
                                currentStep: activeStep?.name,
                                newStep: 'Request Details',
                                newDescription: 'There are no duplicates',
                            })
                            handleChangeOpen(false)
                        },
                        type: 'question',
                    })
                } else {
                    updateStepState({
                        steps,
                        setSteps,
                        currentStep: activeStep?.name,
                        newStep: 'Request Details',
                        newDescription: selectedAssetName,
                    })
                }
                break

            case 'Request Details':
                if (!formRef?.current) {
                    setError('Missing request details')
                    return
                }
                break

            default:
                return null
        }
        setError(null)
    }
}

export const handleCancel = history => {
    removeStepsAndRequests()
    history?.push(AppRoutes?.createRequestStepper())
}

export const handlePreviousStep = ({
    steps,
    setSteps,
    currentStepName,
    history,
    listOfRequests,
    setSelectedAsset,
}) => {
    const activeStepIndex = steps?.findIndex(
        step => step.name === currentStepName,
    )

    // If we're at the Select Asset step, just cancel and go back to the start
    if (activeStepIndex === 1) {
        handleCancel(history)
        return
    }

    // Check if we're in the Request Details step (index 3) and listOfRequests is empty
    // If so, we should skip back to Select Asset (index 1) instead of Check Duplicates (index 2)
    const skipCheckDuplicates =
        currentStepName === 'Request Details' &&
        (!listOfRequests || listOfRequests.length === 0)

    // The target index we want to go back to
    const targetIndex = skipCheckDuplicates ? 1 : activeStepIndex - 1

    // If going back to Select Asset, clear any equipmentId from URL and reset selected asset
    if (targetIndex === 1) {
        // Remove equipmentId from URL
        const currentUrl = new URL(window.location.href)
        const params = new URLSearchParams(currentUrl.search)
        params.delete('equipmentId')

        // Replace the current URL without the equipmentId
        const newUrl = `${currentUrl.pathname}${
            params.toString() ? '?' + params.toString() : ''
        }`
        window.history.replaceState({}, document.title, newUrl)

        // Clear selected asset if the function is provided
        if (setSelectedAsset) {
            setSelectedAsset([])
        }
    }

    const updatedSteps = steps.map((step, index) => {
        if (index === targetIndex) {
            // This is our target previous step, make it active
            return { ...step, active: true, finish: false, description: '' }
        } else if (index === 2 && skipCheckDuplicates) {
            // If skipping Check Duplicates, ensure it's properly marked as inactive
            return { ...step, active: false, finish: false, description: '' }
        } else if (index > targetIndex) {
            // All steps after our target should be inactive and not finished
            return { ...step, active: false, finish: false }
        }
        // Steps before our target should be inactive but finished
        return { ...step, active: false, finish: true }
    })

    setSteps(updatedSteps)
}

export const enforceCheckDuplicatesStep = selectedAssetName => {
    // Create a default steps array with the enforced state.
    const enforcedSteps = [
        {
            name: 'Type of Request',
            description: 'Request Repair',
            finish: true,
            active: false,
        },
        {
            name: 'Select Asset',
            description: selectedAssetName,
            finish: true,
            active: false,
        },
        {
            name: 'Check Duplicates',
            description: '',
            finish: false,
            active: true,
        },
        {
            name: 'Request Details',
            description: '',
            finish: false,
            active: false,
        },
    ]

    localStorage.setItem(
        'repairRequestState',
        JSON.stringify({ steps: enforcedSteps }),
    )

    return enforcedSteps
}
